import { Button, Input, Spin, notification } from 'antd';
import cx from 'classnames';
import React, { useState } from 'react';
import { batch } from 'react-redux';
import { deviceAdminApi } from '../../../api';
import { BadgeListInfo } from '../../../model/BadgeModel';
import { ResponseStatusCode } from '../../../model/CommonModel';
import { DeviceObjectType } from '../../../model/DeviceAdminModel';
import { Modal } from '../../common';
import styles from './editNameModal.module.scss';

type Props = {
	badge: BadgeListInfo;
	maxLength: number;
	onCancel: () => void;
	onConfirm: () => void;
};

const nameInputId = 'EditNameModal-Badge-Studio';

const EditNameModal: React.FC<Props> = ({ badge, maxLength, onCancel, onConfirm }) => {
	const [loading, setLoading] = useState<boolean>(false);
	const [name, setName] = useState<string>(badge.Name);
	const [isNameEmpty, setIsNameEmpty] = useState<boolean>(false);
	const [isNameError, setIsNameError] = useState<boolean>(false);
	const [isNameStarted, setIsNameStarted] = useState<boolean>(false);

	const handleNameInputChange = (value: string) => {
		batch(() => {
			setName(value);
			setIsNameEmpty(value.length === 0);
			setIsNameStarted(true);
		});
	};

	const handleSaveButtonClick = () => {
		setIsNameError(false);
		setLoading(true);
		deviceAdminApi
			.renameDevice(DeviceObjectType.Badge, name.trim(), badge.Id)
			.then(response => {
				if (response.ResponseStatusCode === ResponseStatusCode.Success) {
					onConfirm();
				} else {
					const errorMessage = response.ErrorMessage
						? response.ErrorMessage
						: response.ResponseStatusCode === ResponseStatusCode.PermissionError
						? response.ResponseErrorDescription
						: _('InvalidName');
					notification['error']({
						message: errorMessage,
					});
					setIsNameError(true);
				}
			})
			.finally(() => {
				setLoading(false);
			});
	};

	const showNameEmptyError = (isNameEmpty && isNameStarted) || isNameError;
	const isNameChanged = badge.Name !== name.trim();

	return (
		<Modal
			keyboard={true}
			maskClosable={false}
			onCancel={onCancel}
			onClickOk={() => null}
			width={600}
			title={_('EditName')}
			visible
			customZoomClass={styles.WithModalZoom}
			hideCloseButton={false}
			footer={[
				<Button
					id='editBadgeNameModalSaveButton'
					title={_('Save')}
					key='add-new-instance-save'
					type='primary'
					disabled={isNameEmpty || !isNameChanged}
					onClick={handleSaveButtonClick}>
					{_('SaveChanges')}
				</Button>,
				<Button id='editBadgeNameModalCancelButton' key='add-new-instance-cancel' type='default' onClick={onCancel} disabled={false}>
					{_('Cancel')}
				</Button>,
			]}>
			<Spin tip={`${_('Loading')}...`} spinning={loading} size='large'>
				<div className={styles.container}>
					<label id='editBadgeNameModalNameLabel' htmlFor={nameInputId} className={styles.labelSection}>
						{_('Name')}
					</label>
					<Input
						id={nameInputId}
						key={'key-editBadgeNameModalNameInput'}
						value={name}
						onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleNameInputChange(e.target.value)}
						maxLength={maxLength}
						className={cx('', {
							[styles.error]: showNameEmptyError,
						})}
						autoComplete='off'
					/>
				</div>
			</Spin>
		</Modal>
	);
};

export { EditNameModal };
